import { Link } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ExternalLink, Calendar, Tag, Smartphone, LogOut, ShieldCheck, ChevronRight } from "lucide-react";

function PortalInner() {
  const { user, logout } = useAuth();
  const { settings } = useSite();

  // Member menu organized into collapsible groups (first group open by default).
  const groups = [
    {
      value: "training",
      label: "Training & Booking",
      items: [
        { label: "Book a Session", icon: Calendar, href: settings.exercise_booking_url, external: true },
        { label: "Full Client Portal", icon: ExternalLink, href: settings.client_portal_url, external: true },
      ],
    },
    {
      value: "packages",
      label: "Packages & App",
      items: [
        { label: "View Packages", icon: Tag, to: "/packages" },
        { label: "Download App", icon: Smartphone, to: "/contact" },
      ],
    },
    {
      value: "account",
      label: "Account",
      items: [
        ...(user?.role === "admin"
          ? [{ label: "Admin Dashboard", icon: ShieldCheck, to: "/admin" }]
          : []),
        { label: "Log Out", icon: LogOut, onClick: logout, testid: "portal-logout-button" },
      ],
    },
  ];

  const rowClass =
    "group flex items-center gap-4 rounded-lg border border-border bg-card px-4 py-3.5 transition-colors hover:border-[hsl(var(--brand-red))] hover:bg-accent/40";
  const rowInner = (item) => {
    const Icon = item.icon;
    return (
      <>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand text-white">
          <Icon className="h-5 w-5" />
        </span>
        <span className="flex-1 font-condensed text-lg font-semibold uppercase tracking-wide">{item.label}</span>
        <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-brand" />
      </>
    );
  };

  const renderItem = (item) => {
    if (item.onClick) {
      return (
        <button key={item.label} type="button" onClick={item.onClick} data-testid={item.testid} className={`${rowClass} w-full text-left`}>
          {rowInner(item)}
        </button>
      );
    }
    if (item.external) {
      return (
        <a key={item.label} href={item.href} target="_blank" rel="noopener noreferrer" data-testid={`portal-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={rowClass}>
          {rowInner(item)}
        </a>
      );
    }
    return (
      <Link key={item.label} to={item.to} data-testid={`portal-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={rowClass}>
        {rowInner(item)}
      </Link>
    );
  };

  return (
    <div>
      <PageHeader eyebrow="Members Area" titleLeft="MY " titleRight="PORTAL" description={`Welcome, ${user?.name || user?.email}`} image="https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=1600&q=70&auto=format&fit=crop" />
      <section className="bg-background py-16 sm:py-20">
        <div className="mx-auto w-full max-w-3xl px-4 sm:px-6">
          <Accordion type="multiple" defaultValue={["training"]} className="space-y-3" data-testid="portal-menu">
            {groups.map((g) => (
              <AccordionItem
                key={g.value}
                value={g.value}
                data-testid={`portal-group-${g.value}`}
                className="overflow-hidden rounded-xl border border-border bg-card/40"
              >
                <AccordionTrigger className="px-5 font-condensed text-lg font-semibold uppercase tracking-wide hover:no-underline">
                  {g.label}
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                  <div className="flex flex-col gap-2.5">{g.items.map(renderItem)}</div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}

export default function Portal() {
  return <ProtectedRoute><PortalInner /></ProtectedRoute>;
}
